import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, uploadFile } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useParams2 } from "../contexts/ParamsContext";
import { buildMarkerIcon, STATUS_COLORS } from "./signalIcons";

const CATEGORIES = [
  ["voirie", "Voirie / chaussée"],
  ["proprete", "Propreté / déchets"],
  ["eclairage", "Éclairage"],
  ["espaces_verts", "Espaces verts"],
  ["mobilier", "Mobilier urbain"],
  ["autre", "Autre"],
];

const STATUTS = {
  nouveau: { label: "Nouveau", color: STATUS_COLORS.nouveau },
  pris_en_compte: { label: "Pris en compte", color: STATUS_COLORS.pris_en_compte },
  en_cours: { label: "En cours", color: STATUS_COLORS.en_cours },
  resolu: { label: "Résolu", color: STATUS_COLORS.resolu },
};

const SIGNAL_UPLOADS = "/uploads/signalements/";

// Compression d'une image cote navigateur (max 1500px, jpeg q=0.8)
function compressImage(file, maxSize = 1500, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Lecture impossible"));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Image invalide"));
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          if (width > height) { height = Math.round((height * maxSize) / width); width = maxSize; }
          else { width = Math.round((width * maxSize) / height); height = maxSize; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (!blob) return reject(new Error("Compression échouée"));
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg",
                           { type: "image/jpeg" }));
        }, "image/jpeg", quality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function Signalements() {
  const { user } = useAuth();
  const params = useParams2();
  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const markersLayer = useRef(null);
  const tempMarker = useRef(null);

  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newPos, setNewPos] = useState(null);   // {lat, lng}
  const [filtreCat, setFiltreCat] = useState("");
  const [filtreStatut, setFiltreStatut] = useState("");

  const lat = parseFloat(params.carte_lat || "48.1097");
  const lon = parseFloat(params.carte_lon || "0.0856");

  // Init carte
  useEffect(() => {
    const L = window.L;
    if (!L || !mapRef.current || leafletMap.current) return;

    leafletMap.current = L.map(mapRef.current).setView([lat, lon], 14);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    }).addTo(leafletMap.current);
    markersLayer.current = L.layerGroup().addTo(leafletMap.current);

    // Clic sur la carte = ouvre directement la pop-up de signalement,
    // pre-positionnee a l'endroit clique.  [signal-popup]
    leafletMap.current.on("click", (e) => {
      setNewPos({ lat: e.latlng.lat, lng: e.latlng.lng });
      setCreating(true);
    });

    return () => {
      if (leafletMap.current) { leafletMap.current.remove(); leafletMap.current = null; }
    };
    // eslint-disable-next-line
  }, []);

  // Hack pour acceder a `creating` dans le handler de clic Leaflet
  const creatingRef = useRef(false);
  useEffect(() => { creatingRef.current = creating; }, [creating]);

  // Charger les signalements
  const load = useCallback(async () => {
    try { const d = await api.get("/signalements"); setItems(d.items || []); }
    catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Afficher les marqueurs
  useEffect(() => {
    const L = window.L;
    if (!L || !markersLayer.current) return;
    markersLayer.current.clearLayers();
    items
      .filter((s) => !filtreCat || s.categorie === filtreCat)
      .filter((s) => !filtreStatut || s.statut === filtreStatut)
      .forEach((s) => {
        const m = L.marker([s.latitude, s.longitude], {
          icon: buildMarkerIcon(L, s.categorie, s.statut),
        });
        m.on("click", () => setSelected(s));
        m.addTo(markersLayer.current);
      });
  }, [items, filtreCat, filtreStatut]);

  // Marqueur temporaire (creation)
  useEffect(() => {
    const L = window.L;
    if (!L || !leafletMap.current) return;
    if (tempMarker.current) { tempMarker.current.remove(); tempMarker.current = null; }
    if (newPos) {  // [signal-popup]
      tempMarker.current = L.marker([newPos.lat, newPos.lng], {
        icon: buildMarkerIcon(L, "autre", "nouveau", { selected: true }),
      }).addTo(leafletMap.current);
    }
  }, [newPos]);  // [signal-popup]

  const cancelCreate = () => {  // [signal-popup]
    setCreating(false); setNewPos(null);
  };

  return (
    <div className="container">
      <h1>Signalements citoyens</h1>
      <p className="muted mb-2">
        Signalez un problème sur la commune (nid de poule, dépôt sauvage, éclairage en panne…).
        {user
          ? " Votre signalement sera publié immédiatement."
          : " Sans compte, votre signalement sera publié après validation par la mairie."}
      </p>

      <div className="signal-toolbar mb-2">
        <div className="signal-filters">
          <select value={filtreCat} onChange={(e) => setFiltreCat(e.target.value)}>
            <option value="">Toutes catégories</option>
            {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)}>
            <option value="">Tous statuts</option>
            {Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        {/* [signal-popup] le clic sur la carte ouvre la pop-up ; plus de bouton de mode */}
      </div>

      <div className="signal-instruct mb-2">  {/* [signal-popup] */}
        👆 Cliquez sur la carte à l'emplacement du problème : un formulaire s'ouvrira.
      </div>

      <div ref={mapRef} className="signal-map" />

      <div className="signal-legend">
        {Object.entries(STATUTS).map(([k, v]) => (
          <span key={k} className="signal-legend-item">
            <span className="signal-pin" style={{ background: v.color }} /> {v.label}
          </span>
        ))}
      </div>

      {creating && newPos && (
        <div className="modal-backdrop" onClick={() => { cancelCreate(); }}>
          <div className="modal signal-modal" onClick={(e) => e.stopPropagation()}>
            <SignalForm
              user={user}
              pos={newPos}
              onClose={() => { cancelCreate(); load(); }}
            />
          </div>
        </div>
      )}

      {selected && (
        <SignalDetail signal={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function SignalForm({ user, pos, onClose }) {
  const [f, setF] = useState({
    titre: "", description: "", categorie: "voirie",
    auteur_nom: user ? "" : "", auteur_email: "",
  });
  // Captcha simple : 2 nombres aleatoires
  const [captcha] = useState({
    a: Math.floor(Math.random() * 9) + 1,
    b: Math.floor(Math.random() * 9) + 1,
  });
  const [captchaResp, setCaptchaResp] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const onPhotoChange = async (e) => {
    const f0 = e.target.files?.[0];
    if (!f0) { setPhotoFile(null); setPhotoPreview(null); return; }
    try {
      const compressed = await compressImage(f0);
      setPhotoFile(compressed);
      setPhotoPreview(URL.createObjectURL(compressed));
    } catch (er) {
      setError("Image illisible : " + er.message);
    }
  };

  const submit = async () => {
    setError("");
    if (!f.titre.trim() || f.titre.trim().length < 3) {
      setError("Indiquez un titre court (au moins 3 caractères)."); return;
    }
    if (!user && !f.auteur_nom.trim()) {
      setError("Indiquez votre nom (ou un pseudonyme)."); return;
    }
    if (!user) {
      const r = parseInt(captchaResp, 10);
      if (!Number.isFinite(r) || r !== captcha.a + captcha.b) {
        setError("Vérification anti-spam incorrecte."); return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        titre: f.titre.trim(),
        description: f.description.trim(),
        categorie: f.categorie,
        latitude: pos.lat,
        longitude: pos.lng,
        auteur_nom: f.auteur_nom.trim() || null,
        auteur_email: f.auteur_email.trim() || null,
      };
      if (!user) {
        payload.captcha_a = captcha.a;
        payload.captcha_b = captcha.b;
        payload.captcha_resp = parseInt(captchaResp, 10);
      }
      const r = await api.post("/signalements", payload);
      if (photoFile) {
        try { await uploadFile(`/signalements/${r.id}/photo`, photoFile); } catch {}
      }
      setDone(r.message || "Signalement enregistré.");
    } catch (e) {
      setError(e.message || "Erreur lors de l'envoi.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div>  {/* [signal-popup] */}
        <div className="success">{done}</div>
        <button className="mt-2" onClick={onClose}>Fermer</button>
      </div>
    );
  }

  return (
    <div>  {/* [signal-popup] cadre fourni par le modal */}
      <h3>Nouveau signalement</h3>
      <p className="muted">Position : {pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}</p>
      {error && <div className="error">{error}</div>}
      <div className="field"><label>Titre court *</label>
        <input value={f.titre} onChange={set("titre")} maxLength={160}
               placeholder="Ex : Nid de poule rue du Lavoir" /></div>
      <div className="row">
        <div className="field"><label>Catégorie</label>
          <select value={f.categorie} onChange={set("categorie")}>
            {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></div>
      </div>
      <div className="field"><label>Description (facultatif)</label>
        <textarea rows={3} value={f.description} onChange={set("description")} maxLength={2000} /></div>

      <div className="field">
        <label>Photo (facultatif, optimisée automatiquement)</label>
        <input type="file" accept="image/*" onChange={onPhotoChange} />
        {photoPreview && <img src={photoPreview} alt=""
          style={{ maxWidth: 200, marginTop: 8, borderRadius: 6 }} />}
      </div>

      {!user && (
        <>
          <div className="row">
            <div className="field"><label>Votre nom (ou pseudonyme) *</label>
              <input value={f.auteur_nom} onChange={set("auteur_nom")} maxLength={120} /></div>
            <div className="field"><label>Email (facultatif, pour être informé)</label>
              <input type="email" value={f.auteur_email} onChange={set("auteur_email")} /></div>
          </div>
          <div className="field">
            <label>Vérification anti-spam : combien font {captcha.a} + {captcha.b} ?</label>
            <input type="number" value={captchaResp}
                   onChange={(e) => setCaptchaResp(e.target.value)}
                   style={{ maxWidth: 120 }} />
          </div>
        </>
      )}

      <div className="modal-actions" style={{ marginTop: "1rem" }}>
        <button className="secondary" onClick={onClose}>Annuler</button>
        <button onClick={submit} disabled={busy}>
          {busy ? "Envoi…" : "Envoyer le signalement"}
        </button>
      </div>
    </div>
  );
}

function SignalDetail({ signal, onClose }) {
  const s = signal;
  const st = STATUTS[s.statut] || { label: s.statut, color: "#666" };
  const dt = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR") : "";
  const catLabel = (CATEGORIES.find((c) => c[0] === s.categorie) || [])[1] || s.categorie;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{s.titre}</h2>
        <p>
          <span className="badge" style={{ background: st.color, color: "#fff" }}>
            {catLabel}
          </span>{" "}
          <span className="badge" style={{ background: st.color, color: "#fff" }}>
            {st.label}
          </span>
        </p>
        <p className="muted">
          Signalé le {dt(s.created_at)} par {s.auteur_nom || "Anonyme"}
          {s.resolved_at && ` · Résolu le ${dt(s.resolved_at)}`}
        </p>
        {s.description && <p className="mt-2" style={{ whiteSpace: "pre-wrap" }}>{s.description}</p>}
        {s.photo_filename && (
          <img src={SIGNAL_UPLOADS + s.photo_filename} alt=""
               style={{ width: "100%", maxWidth: 480, borderRadius: 6, marginTop: 12 }} />
        )}
        {s.commentaire_mairie && (
          <div className="signal-commentaire mt-2">
            <strong>Mot de la mairie :</strong>
            <p style={{ whiteSpace: "pre-wrap" }}>{s.commentaire_mairie}</p>
          </div>
        )}
        <div className="modal-actions">
          <button className="secondary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
